import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatIst, inboxApi, type CatalogProduct } from '@wa/shared';

interface Props {
  open: boolean;
  onClose: () => void;
  onSend: (body: { body: string; header: string | null; footer: string | null; retailerIds: string[]; wholeCatalogue: boolean }) => void;
  isSending: boolean;
  sendError: string | null;
}

const MAX = 30;

/**
 * Sending the catalogue into a chat.
 *
 * Three shapes, chosen by what is ticked: nothing ticked sends the catalogue
 * button (the customer browses everything); one product sends a card with its
 * picture and price; two to thirty send a list. The agent does not pick the
 * shape — it falls out of the selection, the same rule the server applies.
 *
 * The products are the mirrored catalogue. When the last refresh failed the
 * previous copy is shown and the reason is printed, because "no products" and
 * "the token cannot read the catalogue" need different people to fix them.
 */
export function ProductPicker({ open, onClose, onSend, isSending, sendError }: Props) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<string[]>([]);
  const [body, setBody] = useState('');
  const [header, setHeader] = useState('');
  const searchBox = useRef<HTMLInputElement>(null);

  const catalogue = useQuery({
    queryKey: ['catalogue'],
    queryFn: () => inboxApi.catalogProducts(),
    staleTime: 300_000,
    enabled: open,
  });

  const refresh = useMutation({
    mutationFn: inboxApi.syncCatalogue,
    onSuccess: (data) => queryClient.setQueryData(['catalogue'], data),
  });

  useEffect(() => {
    if (open) { setChosen([]); setBody(''); setHeader(''); setSearch(''); setTimeout(() => searchBox.current?.focus(), 0); }
  }, [open]);

  const items = catalogue.data?.items ?? [];
  const term = search.trim().toLowerCase();
  const shown = useMemo(
    () => items.filter((p) => !term || p.name.toLowerCase().includes(term) || p.retailerId.toLowerCase().includes(term)),
    [items, term],
  );
  const byId = useMemo(() => new Map(items.map((p) => [p.retailerId, p])), [items]);

  if (!open) return null;

  const state = catalogue.data?.state;
  const noCatalogue = !!state && !state.catalogId && !state.syncProblem;
  const shape = chosen.length === 0 ? 'catalogue' : chosen.length === 1 ? 'one' : 'list';
  const canSend = body.trim().length > 0 && !isSending && !noCatalogue;

  const toggle = (id: string) =>
    setChosen((c) => (c.includes(id) ? c.filter((x) => x !== id) : c.length >= MAX ? c : [...c, id]));

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div
        role="dialog" aria-label="Send products" onClick={(e) => e.stopPropagation()}
        className="flex h-[92vh] w-full max-w-3xl flex-col rounded-t-(--radius-panel) bg-(--color-surface) shadow-(--shadow-pop) sm:h-[80vh] sm:rounded-(--radius-panel)"
      >
        <header className="flex shrink-0 items-center gap-3 border-b border-(--color-line) px-4 py-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-lead font-semibold">Send products</h2>
            <p className="truncate text-small text-(--color-muted)">
              {state?.syncedAt ? `${state.productCount} in the catalogue · refreshed ${formatIst(state.syncedAt)}` : 'From your Commerce Manager catalogue'}
            </p>
          </div>
          <button type="button" onClick={() => refresh.mutate()} disabled={refresh.isPending} className="tap rounded-(--radius-field) border border-(--color-line) px-3 py-1.5 text-small text-(--color-ink-soft) hover:bg-(--color-sunken) disabled:opacity-50">
            {refresh.isPending ? 'Refreshing…' : 'Refresh'}
          </button>
          <button type="button" onClick={onClose} aria-label="Close" className="tap grid size-9 place-items-center rounded-full text-(--color-muted) hover:bg-(--color-sunken)">
            <svg viewBox="0 0 20 20" className="size-5" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
          </button>
        </header>

        {(sendError || state?.syncProblem) && (
          <p role="alert" className="mx-4 mt-3 rounded-(--radius-field) border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
            {sendError ?? state?.syncProblem}
          </p>
        )}

        <div className="grid min-h-0 flex-1 sm:grid-cols-[minmax(0,1fr)_18rem]">
          {/* Products */}
          <div className="flex min-h-0 flex-col border-b border-(--color-line) sm:border-r sm:border-b-0">
            <div className="px-4 pt-3 pb-2">
              <input ref={searchBox} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products" aria-label="Search products"
                className="w-full rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-3 py-2 text-body outline-none focus:border-(--color-brand)" />
            </div>
            <ul className="pane-scroll min-h-0 flex-1 overflow-y-auto px-2 pb-2">
              {catalogue.isPending && <li className="px-2 py-6 text-center text-small text-(--color-muted)">Loading the catalogue…</li>}
              {noCatalogue && <li className="px-2 py-6 text-center text-small text-(--color-muted)">No catalogue is connected to this number. Connect one in WhatsApp Manager → Catalogue.</li>}
              {!catalogue.isPending && !noCatalogue && items.length === 0 && <li className="px-2 py-6 text-center text-small text-(--color-muted)">The catalogue has no products yet.</li>}
              {shown.map((p) => {
                const on = chosen.includes(p.retailerId);
                const full = !on && chosen.length >= MAX;
                return (
                  <li key={p.retailerId}>
                    <label className={`flex cursor-pointer items-center gap-3 rounded-(--radius-field) px-2 py-2 hover:bg-(--color-sunken) ${on ? 'bg-(--color-brand-wash)' : ''} ${full ? 'opacity-50' : ''}`}>
                      <input type="checkbox" checked={on} disabled={full} onChange={() => toggle(p.retailerId)} className="size-4.5 accent-(--color-brand)" />
                      <Thumb product={p} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-body">{p.name}</span>
                        <span className="block truncate text-small text-(--color-muted)">
                          {p.price ?? '—'}
                          {p.availability && p.availability !== 'in stock' ? ` · ${p.availability}` : ''}
                          {p.reviewStatus && p.reviewStatus !== 'approved' ? ` · ${p.reviewStatus}` : ''}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
              {items.length > 0 && shown.length === 0 && <li className="px-2 py-6 text-center text-small text-(--color-muted)">Nothing matches.</li>}
            </ul>
          </div>

          {/* The message */}
          <form className="flex min-h-0 flex-col gap-3 overflow-y-auto p-4" onSubmit={(e) => { e.preventDefault(); if (canSend) onSend({ body: body.trim(), header: header.trim() || null, footer: null, retailerIds: chosen, wholeCatalogue: chosen.length === 0 }); }}>
            <p className="text-small text-(--color-muted)">
              {shape === 'catalogue' && 'Nothing ticked: sends a button that opens the whole catalogue.'}
              {shape === 'one' && `Sends ${byId.get(chosen[0]!)?.name ?? 'the product'} as a card with its picture and price.`}
              {shape === 'list' && `Sends a list of ${chosen.length} products (up to ${MAX}).`}
            </p>

            {shape === 'list' && (
              <label className="block">
                <span className="text-small text-(--color-muted)">Title above the list</span>
                <input value={header} onChange={(e) => setHeader(e.target.value)} maxLength={60} placeholder="Products"
                  className="mt-1 w-full rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-3 py-2 text-body outline-none focus:border-(--color-brand)" />
              </label>
            )}

            <label className="block">
              <span className="text-small text-(--color-muted)">Message</span>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} maxLength={1024} placeholder="Here are the products we talked about."
                className="mt-1 w-full resize-none rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-3 py-2 text-body outline-none focus:border-(--color-brand)" />
            </label>

            {chosen.length > 0 && (
              <ul className="flex flex-wrap gap-1.5">
                {chosen.map((id) => (
                  <li key={id}>
                    <button type="button" onClick={() => toggle(id)} className="tap rounded-(--radius-chip) bg-(--color-sunken) px-2 py-0.5 text-small hover:bg-(--color-line)" title="Remove">
                      {byId.get(id)?.name ?? id} ×
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-auto flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="tap rounded-(--radius-field) px-3 py-2 text-body font-medium text-(--color-muted) hover:bg-(--color-sunken)">Cancel</button>
              <button type="submit" disabled={!canSend} className="tap rounded-(--radius-field) bg-(--color-brand) px-4 py-2 text-body font-semibold text-(--color-brand-ink) hover:bg-(--color-brand-strong) disabled:opacity-40">
                {isSending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function Thumb({ product }: { product: CatalogProduct }) {
  return product.imageUrl
    ? <img src={product.imageUrl} alt="" className="size-10 shrink-0 rounded object-cover" loading="lazy" />
    : <span className="grid size-10 shrink-0 place-items-center rounded bg-(--color-sunken) text-(--color-muted)" aria-hidden="true">🛍️</span>;
}
